// The treaty intents (peace, pact, alliance), kept out of useSimIntents to stay
// under the 400-line limit. Each answers whether the sim took it; the result
// shows in `globals.diplomacy` from the next tick.

import type { createRustSimAdapter } from '@aod/sim-adapter';
import { type RefObject, useMemo } from 'react';

export type TreatyKind = 'peace' | 'pact' | 'alliance';

export interface TreatyActions {
  /** Offer a treaty to another house. */
  readonly propose: (toPlayer: number, treaty: TreatyKind) => boolean;
  /** Accept the offer `fromPlayer` made. */
  readonly accept: (fromPlayer: number) => boolean;
  /** Refuse it. */
  readonly decline: (fromPlayer: number) => boolean;
  /** Go to war (from a pact or alliance it costs honor). */
  readonly declareWar: (toPlayer: number) => boolean;
}

/** What a sim that has not booted offers: nothing happens. */
export const NO_TREATIES: TreatyActions = {
  propose: () => false,
  accept: () => false,
  decline: () => false,
  declareWar: () => false,
};

export function useTreatyIntents(
  adapterRef: RefObject<ReturnType<typeof createRustSimAdapter> | null>,
  localPlayerIndex: number,
): TreatyActions {
  return useMemo(() => {
    const send = (body: Record<string, unknown>): boolean => {
      const adapter = adapterRef.current;
      if (!adapter) return false;
      return adapter.intent({ playerIndex: localPlayerIndex, ...body } as never).ok;
    };
    return {
      propose: (toPlayer, treaty) => send({ kind: 'propose_treaty', toPlayer, treaty }),
      accept: (fromPlayer) => send({ kind: 'accept_treaty', fromPlayer }),
      decline: (fromPlayer) => send({ kind: 'decline_treaty', fromPlayer }),
      declareWar: (toPlayer) => send({ kind: 'declare_war', toPlayer }),
    };
  }, [adapterRef, localPlayerIndex]);
}
